import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import PageBody from "../GeneralComponents/PageBody";
import PageBgBlock from "../GeneralComponents/PageBgBlock";
import GeneralContext from "../GeneralContext/GeneralContext";
import axios from "../../api/axios";
import dayjs from "dayjs";
import {
  GraficoCobertura,
  GraficoComposicion,
  GraficoComposicionRelativa,
  GraficoEstratos,
  GraficoEnviosExtra,
  GraficoIntegridadPorMetrica,
  GraficoLatenciaPorOperador,
  GraficoMapa,
  GraficoPerdidaPorOperador,
  GraficoPorTecnologia,
  GraficoRecepcionPorOperador,
  GraficoRecepcionPorRbd,
  GraficoSerieDiaria,
  GraficoVelocidadPorOperador,
} from "./ReporteCalidadGraficos";

const MESES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

const NOTA_MAPAS = {
  recibidos:
    "Cuánto de lo exigible llegó cada día. Acá el cero se pinta, porque un día sin recepción no es la ausencia de un problema sino el problema: mientras más oscura la celda, más completa la entrega.",
  no_recepcion:
    "La cara complementaria del anterior: los slots que no llegaron. Las celdas en blanco son días sin ninguna falta; cuanto más oscura la celda, mayor la proporción de slots que el operador no reportó ese día.",
  invalidos:
    "Slots que llegaron con alguna métrica negativa, nula o fuera de rango: el equipo reportó, pero no pudo medir. Se corrige en el establecimiento, no en la cadena de transmisión.",
  anomalos:
    "Slots que llegaron y son numéricamente válidos, pero con alguna métrica fuera del parámetro comprometido. Es el mapa que ubica en el calendario a los establecimientos de las secciones de latencia y de pérdida de paquetes.",
  extranos_puros:
    "La parte del mapa anterior que no genera descuento: slots anómalos solo por latencia cero o por una velocidad imposible, sin latencia sobre el umbral ni pérdida sobre 1 %. Son datos basura de la sonda, no incumplimientos. Los días calculados antes de incorporar este desglose se pintan en blanco.",
  duplicados:
    "Mediciones de más sobre los slots reportados. Es el único mapa que puede pasar del 100 %: un operador puede mandar varias mediciones por slot.",
};

const CORTES_CRITICOS = [
  {
    clave: "por_faltantes",
    titulo: "Establecimientos con más slots no recibidos",
    encabezado: "No recibidos",
    nota: "Los establecimientos que más slots dejaron de entregar en el período, en cantidad y no en porcentaje: la columna de recepción está al lado justamente para no confundir al que concentra el problema con el que simplemente tiene más slots exigibles.",
  },
  {
    clave: "por_anomalos",
    titulo: "Establecimientos con más slots anómalos",
    encabezado: "Anómalos",
    nota: "Los que más slots entregaron con alguna métrica fuera del parámetro comprometido. Reportan, y el equipo mide, pero el servicio que se está midiendo no cumple: es la lista que se cruza con latencia y pérdida de paquetes.",
  },
  {
    clave: "por_invalidos",
    titulo: "Establecimientos con más slots inválidos",
    encabezado: "Inválidos",
    nota: "Los que más slots entregaron con valores negativos, nulos o fuera de rango. A diferencia del anterior, acá el problema es el equipo que mide y no el servicio medido.",
  },
];

function periodosDisponibles() {
  const base = dayjs().startOf("month");
  return Array.from({ length: 36 }, (_, i) =>
    base.subtract(i, "month").format("YYYY-MM-01")
  );
}

function etiquetaPeriodo(periodo) {
  if (!periodo) return "—";
  const d = dayjs(periodo);
  return `${MESES[d.month()]} ${d.year()}`;
}

function numero(valor, decimales = 0) {
  if (valor == null) return "—";
  return Number(valor).toLocaleString("es-CL", {
    minimumFractionDigits: decimales,
    maximumFractionDigits: decimales,
  });
}

function porcentaje(valor) {
  if (valor == null) return "—";
  return `${numero(valor, 2)}%`;
}

function nombreOperador(fila) {
  return fila?.nombre_operador || `Operador ${fila?.id_operador}`;
}

function indicadorEstrato(reporte, indicador) {
  return (reporte?.estratos?.indicadores ?? []).find(
    (d) => d.indicador === indicador
  );
}

async function describirError(e) {
  const data = e?.response?.data;
  let detalle = null;
  try {
    if (data instanceof Blob) {
      detalle = JSON.parse(await data.text())?.detail;
    } else if (data) {
      detalle = data.detail;
    }
  } catch {
    detalle = null;
  }
  if (detalle) return detalle;
  if (e?.response?.status === 403) return "No tienes privilegios para ver este reporte.";
  return "No se pudo generar el reporte. Intenta nuevamente.";
}

function Seccion({ titulo, bajada, nota, children }) {
  return (
    <Paper elevation={0} sx={{ border: "1px solid rgba(0,0,0,0.15)", mb: 2.5 }}>
      <Box sx={{ px: 2, py: 1.25, borderBottom: "1px solid rgba(0,0,0,0.12)" }}>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {titulo}
        </Typography>
      </Box>
      {bajada && <Bajada>{bajada}</Bajada>}
      {children}
      {nota && (
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="caption" sx={{ color: "rgba(0,0,0,0.6)" }}>
            {nota}
          </Typography>
        </Box>
      )}
    </Paper>
  );
}

function Bajada({ children }) {
  return (
    <Box sx={{ px: 2, pt: 1.25, pb: 0.25 }}>
      <Typography
        variant="caption"
        sx={{ color: "rgba(0,0,0,0.6)", display: "block" }}
      >
        {children}
      </Typography>
    </Box>
  );
}

function Subtitulo({ children }) {
  return (
    <Box sx={{ px: 2, pt: 2 }}>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {children}
      </Typography>
    </Box>
  );
}

function Tabla({ cabeceras, alineaciones = [], filas }) {
  if (!filas.length) {
    return (
      <Box sx={{ px: 2, py: 1.5 }}>
        <Typography variant="caption" sx={{ color: "rgba(0,0,0,0.6)" }}>
          Sin datos para este corte.
        </Typography>
      </Box>
    );
  }
  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            {cabeceras.map((c, i) => (
              <TableCell
                key={c}
                sx={{ fontWeight: 600 }}
                align={alineaciones[i] || "left"}
              >
                {c}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {filas.map((fila, idx) => (
            <TableRow key={idx} hover>
              {fila.map((valor, i) => (
                <TableCell key={i} align={alineaciones[i] || "left"}>
                  {valor}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

const FILAS_SIN_CALCULAR = 50;

function SeccionSinCalcular({ sinCalcular }) {
  const [expandido, setExpandido] = useState(false);

  const detalle = sinCalcular?.detalle ?? [];
  if (!detalle.length) return null;

  const visibles = expandido ? detalle : detalle.slice(0, FILAS_SIN_CALCULAR);
  const porOperador = sinCalcular.por_operador ?? [];

  return (
    <Seccion
      titulo="Establecimientos sin estadísticas calculadas"
      bajada="Establecimientos que facturan en el período y cuyo cálculo de estadísticas está incompleto o no existe. Mientras estén acá, los totales del informe son parciales: lo que falta se completa desde Admin Area → Estadísticas RBD, en modo insertar faltantes, para los días que correspondan."
    >
      <Box sx={{ px: 2, pt: 1.5 }}>
        <Typography variant="body2">
          {numero(sinCalcular.rbd)} establecimientos con días sin calcular:{" "}
          {numero(sinCalcular.rbd_sin_calculo)} sin ningún día y{" "}
          {numero(sinCalcular.rbd_parciales)} parciales, sobre{" "}
          {numero(sinCalcular.dias_periodo)} días del período.{" "}
          {numero(sinCalcular.rbd_dia_faltantes)} RBD-día faltantes en total.
        </Typography>
      </Box>

      {porOperador.length > 1 && (
        <>
          <Bajada>
            El hueco por operador: sirve para saber si lo que falta calcular es de
            uno solo o si es transversal a todos.
          </Bajada>
          <Tabla
            cabeceras={["Operador", "Sin ningún día", "Parciales", "RBD-día faltantes"]}
            alineaciones={["left", "right", "right", "right"]}
            filas={porOperador.map((f) => [
              f.nombre_operador ?? (f.id_operador ? `Operador ${f.id_operador}` : "Sin operador"),
              numero(f.rbd_sin_calculo),
              numero(f.rbd_parciales),
              numero(f.rbd_dia_faltantes),
            ])}
          />
        </>
      )}

      <Bajada>
        El detalle accionable: cada fila es un establecimiento que hay que
        recalcular, con cuántos días del período tiene y cuántos le faltan.
      </Bajada>
      <Tabla
        cabeceras={["RBD", "Establecimiento", "Operador", "Zona", "Días calculados",
          "Días faltantes"]}
        alineaciones={["right", "left", "left", "left", "right", "right"]}
        filas={visibles.map((f) => [
          f.rbd,
          f.nombre_ees ?? "—",
          f.nombre_operador ?? "—",
          f.nombre_zona ?? "—",
          numero(f.dias_calculados),
          numero(f.dias_faltantes),
        ])}
      />

      {detalle.length > FILAS_SIN_CALCULAR && (
        <Box sx={{ px: 2, pb: 1 }}>
          <Button size="small" sx={{ textTransform: "none" }}
            onClick={() => setExpandido(!expandido)}>
            {expandido
              ? `Mostrar solo los primeros ${FILAS_SIN_CALCULAR}`
              : `Ver los ${numero(detalle.length)} establecimientos`}
          </Button>
        </Box>
      )}
    </Seccion>
  );
}

function SeccionSinReporte({ sinReporte }) {
  const [expandido, setExpandido] = useState(false);

  if (!Array.isArray(sinReporte)) return null;

  const visibles = expandido ? sinReporte : sinReporte.slice(0, FILAS_SIN_CALCULAR);

  return (
    <Seccion
      titulo="Establecimientos sin datos en el período"
      bajada="Establecimientos del operador que facturan en el período, tienen estadísticas calculadas y no entregaron ni un solo slot en todo el mes."
    >
      {sinReporte.length === 0 ? (
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="body2">
            Todos los establecimientos con estadísticas calculadas entregaron al
            menos un slot en el período.
          </Typography>
        </Box>
      ) : (
        <>
          <Box sx={{ px: 2, pt: 1.5 }}>
            <Typography variant="body2">
              {numero(sinReporte.length)} establecimientos sin ningún slot reportado.
            </Typography>
          </Box>
          <Tabla
            cabeceras={["RBD", "Operador", "Zona", "Días calculados",
              "Slots exigibles", "No recibidos"]}
            alineaciones={["right", "left", "left", "right", "right", "right"]}
            filas={visibles.map((f) => [
              f.rbd,
              f.nombre_operador ?? "—",
              f.nombre_zona ?? "—",
              numero(f.dias),
              numero(f.total),
              numero(f.faltantes),
            ])}
          />
          {sinReporte.length > FILAS_SIN_CALCULAR && (
            <Box sx={{ px: 2, pb: 1 }}>
              <Button size="small" sx={{ textTransform: "none" }}
                onClick={() => setExpandido(!expandido)}>
                {expandido
                  ? `Mostrar solo los primeros ${FILAS_SIN_CALCULAR}`
                  : `Ver los ${numero(sinReporte.length)} establecimientos`}
              </Button>
            </Box>
          )}
        </>
      )}
    </Seccion>
  );
}

function medida(valor, unidad) {
  if (valor == null) return "—";
  return `${numero(valor, 2)} ${unidad}`;
}

function TablaSobreNorma({ detalle, total, unidad, referencia }) {
  if (!detalle?.length) return null;
  const restantes = (total ?? 0) - detalle.length;

  return (
    <>
      <Tabla
        cabeceras={["RBD", "Operador", "Zona", referencia, "Mediana",
          "Máxima", "Mediciones válidas", "% slots sobre la norma"]}
        alineaciones={["right", "left", "left", "right", "right", "right",
          "right", "right"]}
        filas={detalle.map((f) => [
          numero(f.rbd),
          f.nombre_operador ?? "—",
          f.nombre_zona ?? "—",
          medida(f.umbral, unidad),
          medida(f.medida, unidad),
          medida(f.maxima, unidad),
          numero(f.mediciones),
          porcentaje(f.porcentaje_sobre_norma),
        ])}
      />
      {restantes > 0 && (
        <Box sx={{ px: 2, pb: 1 }}>
          <Typography variant="caption" sx={{ color: "rgba(0,0,0,0.6)" }}>
            Se listan los {numero(detalle.length)} peores; hay{" "}
            {numero(restantes)} establecimientos más en esta condición.
          </Typography>
        </Box>
      )}
    </>
  );
}

const NOTA_SOBRE_NORMA =
  "La mediana y la máxima dicen dónde quedó la medición típica y cuál fue el peor momento del mes; el porcentaje de slots sobre la norma dice cuánto tiempo estuvo así, contado sobre los slots de la ventana de funcionamiento con el mismo cálculo que marca los descuentos. Un establecimiento con mediana apenas sobre el umbral y 90 % de slots sobre la norma está peor que uno con mediana alta y 5 % de slots. Las mediciones válidas son cuántas mediciones numéricas sustentan la mediana: si son pocas, la mediana dice poco.";

function TablaVelocidad({ corte }) {
  const detalle = corte?.detalle ?? [];
  if (!detalle.length) return null;

  return (
    <>
      <Bajada>
        Los {numero(detalle.length)} establecimientos que menos alcanzaron de su
        velocidad contratada, de menor a mayor. Si la mediana está baja y la
        máxima llega al contrato, el enlace da el ancho de banda pero no lo
        sostiene; si la máxima tampoco llega, nunca lo alcanzó. Los
        establecimientos sin velocidad contratada registrada no aparecen: un
        denominador ausente no es un incumplimiento. El tráfico del mes es el
        volumen estimado en GB: el promedio de cada día por sus mediciones
        válidas por 15 minutos, así que los slots que no llegaron no suman y es
        un piso del volumen real.
      </Bajada>
      <Tabla
        cabeceras={["RBD", "Operador", "Zona", "Contratada", "Mediana",
          "Máxima", "Tráfico mes", "% contratado", "Mediciones"]}
        alineaciones={["right", "left", "left", "right", "right", "right",
          "right", "right", "right"]}
        filas={detalle.map((f) => [
          numero(f.rbd),
          f.nombre_operador ?? "—",
          f.nombre_zona ?? "—",
          medida(f.contratada, "Mbps"),
          medida(f.medida, "Mbps"),
          medida(f.maxima, "Mbps"),
          medida(f.trafico_gb, "GB"),
          porcentaje(f.ratio),
          numero(f.mediciones),
        ])}
      />
    </>
  );
}

function TablaCriticos({ filas, encabezado, formato }) {
  return (
    <Tabla
      cabeceras={["RBD", "Operador", "Zona", "Exigibles", "Reportados",
        "Recepción", encabezado]}
      alineaciones={["right", "left", "left", "right", "right", "right", "right"]}
      filas={filas.map((f) => [
        numero(f.rbd),
        f.nombre_operador ?? "—",
        f.nombre_zona ?? "—",
        numero(f.total),
        numero(f.reportados),
        porcentaje(f.porcentaje_recepcion),
        formato(f),
      ])}
    />
  );
}

export default function ReporteCalidad() {
  const { setPageTitle } = useContext(GeneralContext);

  const periodos = useMemo(() => periodosDisponibles(), []);
  const [periodo, setPeriodo] = useState(periodos[1] ?? periodos[0]);
  const [idOperador, setIdOperador] = useState("");
  const [ambito, setAmbito] = useState("ventana");

  const [operadores, setOperadores] = useState([]);
  const [reporte, setReporte] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [descargando, setDescargando] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setPageTitle?.("REPORTE DE CALIDAD DE DATOS");
  }, []);

  useEffect(() => {
    axios
      .getAllOperadores()
      .then((res) => setOperadores(res?.data ?? []))
      .catch(() => setOperadores([]));
  }, []);

  const parametros = () => ({
    periodo,
    id_operador: idOperador || undefined,
    ambito,
  });

  const consultar = async () => {
    setCargando(true);
    setError(null);
    setReporte(null);
    try {
      const res = await axios.getReporteCalidad(parametros());
      setReporte(res?.data ?? null);
    } catch (e) {
      setError(await describirError(e));
    } finally {
      setCargando(false);
    }
  };

  const descargarPDF = async () => {
    setDescargando(true);
    setError(null);
    try {
      const res = await axios.getReporteCalidadPDF(parametros());
      const url = window.URL.createObjectURL(
        new Blob([res.data], { type: "application/pdf" })
      );
      const link = document.createElement("a");
      link.href = url;
      const sufijo = idOperador ? `_operador_${idOperador}` : "";
      link.setAttribute("download", `reporte_calidad_${periodo}${sufijo}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError(await describirError(e));
    } finally {
      setDescargando(false);
    }
  };

  const resumen = reporte?.resumen;
  const cobertura = reporte?.cobertura;
  const velocidad = reporte?.velocidad;
  const latencia = reporte?.latencia;
  const perdida = reporte?.perdida;
  const mapas = (reporte?.mapas ?? []).filter((m) => (m.filas ?? []).length > 0);
  const criticos = reporte?.criticos;
  const conforme = indicadorEstrato(reporte, "conforme");
  const recepcion = indicadorEstrato(reporte, "recepcion");
  const velocidades = (velocidad?.metricas ?? []).filter(
    (m) => (m.por_operador ?? []).length > 0 || (m.detalle ?? []).length > 0
  );
  const tramosLatencia = (latencia?.tramos ?? []).filter((t) => t.rbd > 0);

  return (
    <PageBody>
      <Box width="100%">
        <Typography variant="h1" sx={{ textAlign: "center", p: "0.6rem" }}>
          REPORTE DE CALIDAD DE DATOS
        </Typography>
        <Typography variant="body2" sx={{ textAlign: "center", mb: 3 }}>
          Recepción, integridad y conformidad de los datos de supervisión del
          período, sobre la malla de slots de 15 minutos. El universo son los
          establecimientos que facturan en el período (estado de pago
          &quot;Facturar&quot;), el mismo del ETL de prefacturas.
        </Typography>

        <Box
          sx={{
            mb: 3,
            p: 2.5,
            border: "1px solid rgba(0,0,0,0.15)",
            borderRadius: 1,
            background: "#fff",
            display: "flex",
            alignItems: "flex-start",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Box sx={{ flex: "0 0 200px" }}>
            <Typography variant="body1" sx={{ mb: 0.75, fontWeight: 500 }}>
              Período:
            </Typography>
            <TextField
              select
              size="small"
              fullWidth
              value={periodo}
              onChange={(e) => setPeriodo(e.target.value)}
            >
              {periodos.map((p) => (
                <MenuItem key={p} value={p}>
                  {etiquetaPeriodo(p)}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <Box sx={{ flex: "0 0 280px" }}>
            <Typography variant="body1" sx={{ mb: 0.75, fontWeight: 500 }}>
              Operador:
            </Typography>
            <TextField
              select
              size="small"
              fullWidth
              value={idOperador}
              onChange={(e) => setIdOperador(e.target.value)}
              SelectProps={{ displayEmpty: true }}
            >
              <MenuItem value="">Todos los operadores</MenuItem>
              {operadores.map((o) => (
                <MenuItem key={o.id_operador} value={o.id_operador}>
                  {o.nombre_operador}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <Box sx={{ flex: "0 0 250px" }}>
            <Typography variant="body1" sx={{ mb: 0.25, fontWeight: 500 }}>
              Ámbito:
            </Typography>
            <RadioGroup
              row
              value={ambito}
              onChange={(e) => setAmbito(e.target.value)}
            >
              <FormControlLabel
                value="ventana"
                control={<Radio size="small" />}
                label={<Typography variant="body2">Ventana</Typography>}
              />
              <FormControlLabel
                value="dia"
                control={<Radio size="small" />}
                label={<Typography variant="body2">Día completo</Typography>}
              />
            </RadioGroup>
          </Box>

          <Box sx={{ alignSelf: "flex-end", display: "flex", gap: 1 }}>
            <Button
              variant="contained"
              startIcon={
                cargando ? <CircularProgress size={16} /> : <AssessmentOutlinedIcon />
              }
              disabled={cargando}
              onClick={consultar}
              sx={{
                background: "#C8D8F0",
                color: "#1565c0",
                boxShadow: "none",
                textTransform: "none",
                "&:hover": { background: "#b3c8e8", boxShadow: "none" },
              }}
            >
              {cargando ? "Calculando..." : "Ver reporte"}
            </Button>
            <Button
              startIcon={descargando ? <CircularProgress size={16} /> : <DownloadIcon />}
              disabled={cargando || descargando}
              onClick={descargarPDF}
              sx={{ textTransform: "none" }}
            >
              {descargando ? "Generando..." : "Descargar PDF"}
            </Button>
          </Box>
        </Box>

        {cargando && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "30vh",
            }}
          >
            <CircularProgress />
            <Typography sx={{ mt: 2 }}>Calculando métricas del período...</Typography>
          </Box>
        )}

        {!cargando && error && (
          <Paper
            elevation={0}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              p: 2,
              mb: 3,
              border: "1px solid rgba(198, 40, 40, 0.3)",
              background: "rgba(198, 40, 40, 0.08)",
            }}
          >
            <ErrorOutlineIcon sx={{ color: "#C62828" }} fontSize="small" />
            <Typography variant="body2" sx={{ color: "#C62828" }}>
              {error}
            </Typography>
          </Paper>
        )}

        {!cargando && reporte?.aviso && (
          <Paper
            elevation={0}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              p: 2,
              mb: 3,
              border: "1px solid rgba(237, 108, 2, 0.3)",
              background: "rgba(237, 108, 2, 0.08)",
            }}
          >
            <ErrorOutlineIcon sx={{ color: "#ED6C02" }} fontSize="small" />
            <Typography variant="body2" sx={{ color: "#B35300" }}>
              {reporte.aviso}
            </Typography>
          </Paper>
        )}

        {!cargando && reporte && resumen && (
          <PageBgBlock pad="0">
            <Seccion
              titulo={`Resumen — ${etiquetaPeriodo(reporte.periodo)}${
                reporte.nombre_operador ? ` — ${reporte.nombre_operador}` : ""
              } — ámbito ${reporte.nombre_ambito}`}
              bajada="Los totales del período sobre la malla de slots. Recepción: slots reportados sobre exigibles. Integridad: slots correctos sobre reportados. Dato conforme: slots correctos sobre exigibles. Un slot es correcto si llegó, es numéricamente válido y está dentro de los parámetros de la norma."
            >
              <Tabla
                cabeceras={["Indicador", "Valor"]}
                alineaciones={["left", "right"]}
                filas={[
                  ["Establecimientos analizados", numero(resumen.rbd)],
                  ["Operadores involucrados", numero(resumen.operadores)],
                  ["Zonas involucradas", numero(resumen.zonas)],
                  ["Días con datos calculados", numero(resumen.dias_con_datos)],
                  ["Slots exigibles", numero(resumen.total)],
                  ["Slots reportados", numero(resumen.reportados)],
                  ["Mediciones recibidas", numero(resumen.recibidos)],
                  ["Mediciones de más", numero(resumen.extra)],
                  ["Slots no recibidos", numero(resumen.faltantes)],
                  ["Slots inválidos", numero(resumen.invalidos)],
                  ["Slots anómalos", numero(resumen.anomalos)],
                  ["Slots correctos", numero(resumen.correctos)],
                  ["Recepción", porcentaje(resumen.porcentaje_recepcion)],
                  ["Integridad", porcentaje(resumen.porcentaje_integridad)],
                  ["Dato conforme", porcentaje(resumen.porcentaje_conforme)],
                  ["Establecimientos sin ningún reporte", numero(resumen.rbd_sin_reporte)],
                  [
                    `Establecimientos bajo ${numero(resumen.umbral_recepcion)}% de recepción`,
                    numero(resumen.rbd_bajo_umbral),
                  ],
                ]}
              />
              <Bajada>
                El período día a día: la barra clara es lo que llegó y la oscura
                lo que además sirve, las dos sobre lo exigible de ese día. La
                distancia entre ambas es el dato que llegó y no pasó los filtros;
                un valle en las dos a la vez es un día de caída.
              </Bajada>
              <GraficoSerieDiaria reporte={reporte} />
            </Seccion>

            {cobertura && (
              <Seccion
                titulo="Cobertura del cálculo"
                bajada="Compara los RBD-día presentes en stats_rbd_diario contra los esperados: los establecimientos que facturan en el período, por cada día del mes. Un período calculado a medias baja todos los totales sin que eso signifique que el operador reportó menos, así que esta sección se lee antes que cualquier indicador."
              >
                <Tabla
                  cabeceras={["Indicador", "Valor"]}
                  alineaciones={["left", "right"]}
                  filas={[
                    ["Días del período", numero(cobertura.dias_periodo)],
                    ["Establecimientos que facturan en el período", numero(cobertura.rbd_parque)],
                    ["RBD-día esperados", numero(cobertura.rbd_dia_esperados)],
                    ["RBD-día calculados", numero(cobertura.rbd_dia_calculados)],
                    ["Cobertura del cálculo", porcentaje(cobertura.porcentaje)],
                    [
                      "Días sin ningún RBD calculado",
                      numero((cobertura.dias_sin_calculo ?? []).length),
                    ],
                  ]}
                />
                <Bajada>
                  Cada barra es un día del mes y la línea punteada, el total de
                  establecimientos que facturan. Una barra corta es un día
                  calculado a medias, no un día malo del operador: los faltantes
                  se completan desde Admin Area → Estadísticas RBD.
                </Bajada>
                <GraficoCobertura reporte={reporte} />
              </Seccion>
            )}

            <SeccionSinCalcular sinCalcular={reporte.sin_calcular} />

            <SeccionSinReporte sinReporte={reporte.sin_reporte} />

            <Seccion
              titulo="Recepción por operador"
              bajada="Cuánto de lo exigible entregó cada operador, y cómo se reparte esa entrega entre sus establecimientos."
            >
              <Bajada>
                Las cifras exactas del corte. &quot;Sin reporte&quot; son los
                establecimientos del operador que no entregaron ni un solo slot
                en todo el período.
              </Bajada>
              <Tabla
                cabeceras={["Operador", "RBD", "Sin reporte", "Exigibles", "Reportados",
                  "No recib.", "Recepción", "Integridad", "Conforme"]}
                alineaciones={["left", "right", "right", "right", "right", "right",
                  "right", "right", "right"]}
                filas={(reporte.por_operador ?? []).map((f) => [
                  nombreOperador(f),
                  numero(f.rbd),
                  numero(f.rbd_sin_reporte),
                  numero(f.total),
                  numero(f.reportados),
                  numero(f.faltantes),
                  porcentaje(f.porcentaje_recepcion),
                  porcentaje(f.porcentaje_integridad),
                  porcentaje(f.porcentaje_conforme),
                ])}
              />
              <Bajada>
                Los mismos dos porcentajes de la tabla, a escala: a la izquierda
                cuánto llegó de lo exigible, a la derecha cuánto de lo que llegó
                además sirve. Un operador puede estar bien en uno y mal en el
                otro, y se corrigen en lugares distintos.
              </Bajada>
              <GraficoRecepcionPorOperador reporte={reporte} />
              <Bajada>
                La misma recepción, pero establecimiento por establecimiento
                dentro de cada operador. La caja va del primer al tercer cuartil
                con la línea en la mediana, los bigotes son los de Tukey y los
                puntos son los establecimientos que quedan fuera de ellos. Un
                operador con 98 % de promedio puede tener 40 colegios en cero: en
                la barra se ve igual que uno parejo, acá no.
              </Bajada>
              <GraficoRecepcionPorRbd reporte={reporte} />
            </Seccion>

            <Seccion
              titulo="Integridad del dato recibido"
              bajada="De lo que sí llegó, cuánto sirve. Un slot inválido es un dato que el operador no pudo medir; uno anómalo es una medición que está fuera del parámetro comprometido."
            >
              <Bajada>
                Cómo se reparten los slots exigibles de cada operador entre las
                cuatro categorías, en cantidad. Las cuatro son disjuntas y suman
                el total exigible.
              </Bajada>
              <GraficoComposicion reporte={reporte} />
              <Bajada>
                La misma composición llevada a 100 % para comparar operadores de
                tamaños distintos: acá el largo de cada segmento ya no depende de
                cuántos establecimientos tiene el operador.
              </Bajada>
              <GraficoComposicionRelativa reporte={reporte} />
              <Bajada>
                Por métrica, cuántas mediciones llegaron con el valor centinela
                que el origen usa para marcar ausencia de dato. Una métrica sin
                dato válido no siempre implica corte: puede ser un campo que el
                equipo del establecimiento no reporta.
              </Bajada>
              <Tabla
                cabeceras={["Métrica", "Sin dato válido", "Válidas", "% sin dato"]}
                alineaciones={["left", "right", "right", "right"]}
                filas={(reporte.integridad?.por_metrica ?? []).map((f) => [
                  f.nombre,
                  numero(f.sentinela),
                  numero(f.validos),
                  porcentaje(f.porcentaje_sentinela),
                ])}
              />
              <GraficoIntegridadPorMetrica reporte={reporte} />
              {(reporte.integridad?.por_operador ?? []).length > 1 && (
                <>
                  <Bajada>
                    El mismo porcentaje sin dato válido, abierto por operador: si
                    una métrica falta en todos, el problema es del modelo de
                    datos; si falta en uno solo, es de ese operador.
                  </Bajada>
                  <Tabla
                    cabeceras={["Operador", "Latencia", "Bajada", "Subida",
                      "Bajada int.", "Pérdida"]}
                    alineaciones={["left", "right", "right", "right", "right", "right"]}
                    filas={reporte.integridad.por_operador.map((f) => [
                      nombreOperador(f),
                      porcentaje(f.latencia),
                      porcentaje(f.bajada),
                      porcentaje(f.subida),
                      porcentaje(f.interac_bajada),
                      porcentaje(f.perdida_paquetes),
                    ])}
                  />
                </>
              )}
            </Seccion>

            {(reporte.envios_extra?.por_operador ?? []).some((f) => f.extra > 0) && (
              <Seccion
                titulo="Mediciones enviadas de más"
                bajada="Lo esperado es una medición por establecimiento cada 15 minutos. Las mediciones que llegan para un slot ya cubierto no mejoran la recepción y son indicio de reenvío, doble reporte o un equipo emitiendo fuera de cadencia."
              >
                <Bajada>
                  Por operador: cuántas mediciones de más llegaron y en cuántos de
                  sus establecimientos. El porcentaje es sobre los slots que sí
                  tenía que reportar.
                </Bajada>
                <Tabla
                  cabeceras={["Operador", "RBD", "Con envíos de más", "Reportados",
                    "Recibidas", "De más", "% de más"]}
                  alineaciones={["left", "right", "right", "right", "right",
                    "right", "right"]}
                  filas={reporte.envios_extra.por_operador.map((f) => [
                    nombreOperador(f),
                    numero(f.rbd),
                    numero(f.rbd_con_extra),
                    numero(f.reportados),
                    numero(f.recibidos),
                    numero(f.extra),
                    porcentaje(f.porcentaje_extra),
                  ])}
                />
                <Bajada>
                  Los mismos envíos de más en dos lecturas: el volumen absoluto a
                  la izquierda y su peso relativo a la derecha. Un operador chico
                  puede mandar pocas de más y aun así tener el porcentaje más alto.
                </Bajada>
                <GraficoEnviosExtra reporte={reporte} />
                {(reporte.envios_extra?.detalle ?? []).length > 0 && (
                  <>
                    <Bajada>
                      Los establecimientos que concentran los envíos de más: es la
                      lista con la que se va a revisar el equipo o su cadencia.
                    </Bajada>
                    <Tabla
                      cabeceras={["RBD", "Operador", "Zona", "Reportados", "Recibidas",
                        "De más", "%"]}
                      alineaciones={["right", "left", "left", "right", "right",
                        "right", "right"]}
                      filas={reporte.envios_extra.detalle.map((f) => [
                        numero(f.rbd),
                        f.nombre_operador ?? "—",
                        f.nombre_zona ?? "—",
                        numero(f.reportados),
                        numero(f.recibidos),
                        numero(f.extra),
                        porcentaje(f.porcentaje_extra),
                      ])}
                    />
                  </>
                )}
              </Seccion>
            )}

            {velocidades.length > 0 && (
              <Seccion
                titulo="Velocidad medida por operador"
                bajada="Cuánta velocidad movió efectivamente cada establecimiento en cada uno de los tres sentidos, repartida dentro de su operador. La supervisión mide tráfico cursado, no capacidad disponible: un establecimiento que aparece bajo puede tener el servicio operativo y simplemente no estar usándolo, así que esta sección no declara incumplimiento."
                nota={`La velocidad del período de cada establecimiento es el promedio de sus medianas diarias, ponderado por las mediciones válidas de cada día, y viene en bits por segundo: se divide por ${numero(velocidad?.factor_medicion_a_mbps)} para llevarla a Mbps.`}
              >
                {velocidades.map((corte) => (
                  <Box key={corte.clave}>
                    <Subtitulo>{corte.nombre}</Subtitulo>
                    <Bajada>
                      Cada caja es un operador y cada establecimiento suyo es un
                      dato: la caja va del primer al tercer cuartil con la línea
                      en la mediana, los bigotes son los de Tukey y los puntos,
                      los establecimientos que quedan fuera de ellos. Una caja
                      angosta es un parque parejo; una caja larga o una cola
                      pegada al cero significan que el promedio del operador no
                      describe a ninguno de sus establecimientos.
                    </Bajada>
                    <GraficoVelocidadPorOperador reporte={reporte} clave={corte.clave} />
                    <TablaVelocidad corte={corte} />
                  </Box>
                ))}
              </Seccion>
            )}

            {tramosLatencia.length > 0 && (
              <Seccion
                titulo="Latencia por tramo de umbral"
                bajada="La latencia comprometida no es la misma para todos: depende de la tecnología del establecimiento. Un enlace satelital con 650 ms cumple y uno de fibra con 150 ms no, así que la sección se separa en un tramo por umbral y cada uno se lee contra su propia norma."
                nota={
                  latencia?.sin_umbral
                    ? `${numero(latencia.sin_umbral)} establecimientos con latencia medida quedan fuera de la sección por no tener registrado el umbral de su tecnología.`
                    : null
                }
              >
                {tramosLatencia.map((tramo) => (
                  <Box key={tramo.clave}>
                    <Subtitulo>
                      {tramo.nombre} — {numero(tramo.rbd_sobre_umbral)} de{" "}
                      {numero(tramo.rbd)} sobre el umbral
                    </Subtitulo>
                    <Bajada>
                      Cada caja es un operador y cada establecimiento de este
                      tramo, un dato; la línea punteada es el umbral de{" "}
                      {numero(tramo.umbral)} ms: lo que queda a su derecha lo
                      supera. En el período,{" "}
                      {porcentaje(tramo.porcentaje_sobre_norma)} de los{" "}
                      {numero(tramo.slots_ventana)} slots de la ventana de estos
                      establecimientos cruzaron el umbral.
                    </Bajada>
                    <GraficoLatenciaPorOperador tramo={tramo} />
                    {(tramo.detalle ?? []).length > 0 && (
                      <>
                        <Bajada>
                          Los peores casos del tramo, individualizados.{" "}
                          {NOTA_SOBRE_NORMA}
                        </Bajada>
                        <TablaSobreNorma
                          detalle={tramo.detalle}
                          total={tramo.rbd_sobre_umbral}
                          unidad="ms"
                          referencia="Umbral"
                        />
                      </>
                    )}
                  </Box>
                ))}
              </Seccion>
            )}

            {perdida?.rbd > 0 && (
              <Seccion
                titulo="Pérdida de paquetes"
                bajada={`La pérdida de paquetes tiene una sola norma para todo el parque, el ${numero(perdida.umbral)} %, así que se lee en un solo gráfico. ${numero(perdida.rbd_sobre_umbral)} de ${numero(perdida.rbd)} establecimientos con medición quedaron con su mediana del período sobre la norma.`}
              >
                <Bajada>
                  Cada caja es un operador y cada establecimiento suyo, un dato;
                  la línea punteada es el {numero(perdida.umbral)} % de la norma.
                  En el período, {porcentaje(perdida.porcentaje_sobre_norma)} de
                  los {numero(perdida.slots_ventana)} slots de la ventana
                  estuvieron sobre ella.
                </Bajada>
                <GraficoPerdidaPorOperador reporte={reporte} />
                {(perdida.detalle ?? []).length > 0 && (
                  <>
                    <Bajada>
                      Los peores casos, individualizados. {NOTA_SOBRE_NORMA}
                    </Bajada>
                    <TablaSobreNorma
                      detalle={perdida.detalle}
                      total={perdida.rbd_sobre_umbral}
                      unidad="%"
                      referencia="Norma"
                    />
                  </>
                )}
              </Seccion>
            )}

            {conforme?.rbd > 0 && (
              <Seccion
                titulo="Distribución de establecimientos por nivel de entrega"
                bajada="Cuántos establecimientos hay en cada tramo, en vez de un promedio: un operador con la mitad de su parque perfecto y la otra mitad caída tiene el mismo promedio que uno parejo y mediocre, y no es lo mismo. Datos correctos es el porcentaje de los slots exigibles del establecimiento que llegaron, son numéricamente válidos y están dentro de los parámetros de la norma; recepción solo mide si el dato llegó, sin importar cómo."
                nota={
                  conforme.sin_dato > 0
                    ? `${numero(conforme.sin_dato)} establecimientos quedan fuera del conteo por no tener slots exigibles en el ámbito consultado: un denominador cero no es un incumplimiento.`
                    : null
                }
              >
                <Bajada>
                  El conteo por tramo de los dos indicadores. El primer tramo es
                  exactamente cero y los demás incluyen su extremo superior, así
                  que un establecimiento con 90 % queda en el tramo de 80 a 90 %.
                  Un establecimiento que baja en datos correctos y no en recepción
                  está reportando, pero mal.
                </Bajada>
                <Tabla
                  cabeceras={["Estrato", "Datos correctos", "% correctos",
                    "Recepción", "% recepción"]}
                  alineaciones={["left", "right", "right", "right", "right"]}
                  filas={(conforme.filas ?? []).map((f, i) => {
                    const otra = recepcion?.filas?.[i];
                    return [
                      f.etiqueta,
                      numero(f.rbd),
                      porcentaje(f.porcentaje),
                      numero(otra?.rbd),
                      porcentaje(otra?.porcentaje),
                    ];
                  })}
                />
                <Bajada>
                  El tramo de datos correctos repartido por operador: un tramo
                  malo que aporta un solo operador se atiende distinto de uno
                  repartido entre todos.
                </Bajada>
                <GraficoEstratos reporte={reporte} indicador="conforme" />
                <Bajada>
                  El mismo conteo sobre la recepción. Comparado con el anterior,
                  la diferencia entre los dos gráficos es el dato que llegó y no
                  sirvió.
                </Bajada>
                <GraficoEstratos reporte={reporte} indicador="recepcion" />
                {(reporte.estratos?.operadores ?? []).length > 1 && (
                  <>
                    <Bajada>
                      Datos correctos: cuántos establecimientos aporta cada
                      operador a cada tramo, en cifras exactas.
                    </Bajada>
                    <Tabla
                      cabeceras={["Operador", "RBD",
                        ...(conforme.filas ?? []).map((f) => f.etiqueta)]}
                      alineaciones={["left", "right",
                        ...(conforme.filas ?? []).map(() => "right")]}
                      filas={reporte.estratos.operadores.map((operador, posicion) => [
                        nombreOperador(operador),
                        numero(operador.rbd),
                        ...(conforme.filas ?? []).map((f) =>
                          numero((f.por_operador ?? [])[posicion])
                        ),
                      ])}
                    />
                  </>
                )}
              </Seccion>
            )}

            {mapas.length > 0 && (
              <Seccion
                titulo="Mapas del período por día"
                bajada="Varias lecturas del mismo período, día a día y sobre la misma grilla. Las escalas van de 0 a 100 % para que los mapas se comparen entre sí: un mapa autoescalado a su propio máximo hace ver como grave un 3 %. Distingue una falla puntual de una degradación sostenida."
              >
                {mapas.map((mapa) => (
                  <Box key={mapa.clave}>
                    <Subtitulo>{mapa.nombre} (%)</Subtitulo>
                    {NOTA_MAPAS[mapa.clave] && (
                      <Bajada>{NOTA_MAPAS[mapa.clave]}</Bajada>
                    )}
                    <GraficoMapa mapa={mapa} />
                  </Box>
                ))}
              </Seccion>
            )}

            {(reporte.por_zona ?? []).length > 1 && (
              <Seccion
                titulo="Por zona"
                bajada="El mismo período agrupado por zona, con su operador al lado. La recepción dice cuánto llegó y el dato conforme cuánto de eso sirve: la diferencia entre ambas columnas es el dato recibido que no pasó los filtros."
              >
                <Tabla
                  cabeceras={["Operador", "Zona", "RBD", "Exigibles", "Reportados",
                    "Recepción", "Integridad", "Conforme"]}
                  alineaciones={["left", "left", "right", "right", "right", "right",
                    "right", "right"]}
                  filas={reporte.por_zona.map((f) => [
                    f.nombre_operador ?? "—",
                    f.nombre_zona ?? f.id_zona ?? "—",
                    numero(f.rbd),
                    numero(f.total),
                    numero(f.reportados),
                    porcentaje(f.porcentaje_recepcion),
                    porcentaje(f.porcentaje_integridad),
                    porcentaje(f.porcentaje_conforme),
                  ])}
                />
              </Seccion>
            )}

            {(reporte.por_tecnologia ?? []).length > 1 && (
              <Seccion
                titulo="Por tecnología"
                bajada="El mismo período agrupado por tecnología de enlace: separa el problema de red del problema de operador."
              >
                <Tabla
                  cabeceras={["Tecnología", "RBD", "Exigibles", "Reportados",
                    "Recepción", "Integridad", "Conforme"]}
                  alineaciones={["left", "right", "right", "right", "right",
                    "right", "right"]}
                  filas={reporte.por_tecnologia.map((f) => [
                    f.nombre_tecnologia ?? f.tipo_tecnologia ?? "—",
                    numero(f.rbd),
                    numero(f.total),
                    numero(f.reportados),
                    porcentaje(f.porcentaje_recepcion),
                    porcentaje(f.porcentaje_integridad),
                    porcentaje(f.porcentaje_conforme),
                  ])}
                />
                <Bajada>
                  Las tecnologías ordenadas de peor a mejor recepción, con el dato
                  conforme superpuesto: el hueco entre las dos barras es lo que
                  llegó y no sirve.
                </Bajada>
                <GraficoPorTecnologia reporte={reporte} />
              </Seccion>
            )}

            {criticos &&
              CORTES_CRITICOS.filter(
                (corte) => (criticos[corte.clave] ?? []).length > 0
              ).map((corte) => (
                <Seccion
                  key={corte.clave}
                  titulo={corte.titulo}
                  bajada={corte.nota}
                >
                  <TablaCriticos
                    filas={criticos[corte.clave]}
                    encabezado={corte.encabezado}
                    formato={(f) => numero(f.valor)}
                  />
                </Seccion>
              ))}
          </PageBgBlock>
        )}
      </Box>
    </PageBody>
  );
}
